/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement } from 'react';
import { Link } from '@/components/link';
import type { FooterLegal } from '@/lib/content/types';

/**
 * The charcoal legal band, extracted from `main-footer.tsx` so both footer variants render the
 * identical markup, content source, and link set (CRCB2B-48) — see design.md Decision 13 and the
 * "legal bar is a single shared component" requirement in `specs/site-footer/spec.md`.
 *
 * Reads `footer-legal`. Follows the same no-fallback rule as every other content-asset-backed
 * section: when `legal` is `null` (missing, offline, or schema-invalid), nothing renders here — no
 * hardcoded copyright line, no placeholder link. That is a deliberate product decision, not an
 * oversight; see design.md's resolved Open Questions.
 *
 * Renders whatever links the asset supplies (e.g. a single combined "Privacy Policy & Terms of Use"
 * entry) rather than two hardcoded destinations — `legal-links.tsx`'s `/privacy` + `/terms` pair is
 * retired in favour of this asset-supplied set.
 */
export default function LegalBar({ legal }: { legal: FooterLegal | null }): ReactElement | null {
    if (!legal) {
        return null;
    }

    return (
        <div className="bg-brand-black-charcoal">
            <div className="section-container py-6">
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm font-book tracking-sram text-footer-foreground">
                    <p>{legal.copyright}</p>
                    <div className="flex flex-wrap items-center gap-x-6">
                        {legal.links.map((link, index) => (
                            <Link
                                // eslint-disable-next-line react/no-array-index-key -- asset-authored links have no stable id
                                key={`${link.url}-${index}`}
                                to={link.url}
                                className="py-2 hover:text-brand-gray-300 transition-colors">
                                {link.label}
                            </Link>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
